import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LogOut, LayoutDashboard, FilePlus2, UsersRound, KanbanSquare, ListChecks,
  Files, Briefcase, Settings,
} from "lucide-react";
import NotificationBell from "./NotificationBell";
import api from "../lib/api";

/**
 * Application shell + top navigation.
 * Nav order reads left-to-right like a real CRM:
 *   Dashboard → Leads → Clients → Follow-ups → Templates (admin) → Users (admin) → Settings (admin)
 *
 * Client-managers get a tighter nav: no Leads, no Templates, no Users.
 */
export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const navigate = useNavigate();
  const [org, setOrg] = useState(null);

  useEffect(() => {
    // Refresh only when the shell mounts — org rarely changes.
    api.get("/org/public").then((r) => setOrg(r.data)).catch(() => setOrg(null));
  }, []);

  const linkCls = (p) =>
    `flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-sm font-medium transition-colors ${
      loc.pathname === p
        ? "bg-[#E6F4F1] text-[#1A7F6E]"
        : "text-slate-600 hover:text-[#0D1B2A] hover:bg-slate-100"
    }`;

  const isAdmin = user?.role === "admin";
  const isClientManager = user?.role === "client_manager";
  const isConsultant = user?.role === "consultant";
  const canSeeLeads = isAdmin || isConsultant;

  const logoBust = org?.logo_updated_at ? `?v=${new Date(org.logo_updated_at).getTime()}` : "";
  const brandName = org?.name || "Next Provider";

  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAFC]">
      <header className="no-print bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 min-w-0 shrink-0" data-testid="brand-home-link">
            {org?.has_logo ? (
              <img
                src={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/org/logo${logoBust}`}
                alt={brandName}
                className="h-9 max-w-[160px] object-contain"
                data-testid="org-logo-img"
              />
            ) : (
              <span className="wordmark text-base text-[#0D1B2A] flex items-center whitespace-nowrap">
                <span className="np-mark">NP</span>{brandName}
              </span>
            )}
          </Link>

          <nav className="hidden lg:flex items-center gap-0.5 flex-1 justify-center flex-wrap">
            <Link to="/" className={linkCls("/")} data-testid="nav-dashboard-link">
              <LayoutDashboard className="w-4 h-4" />Dashboard
            </Link>
            {canSeeLeads && (
              <Link to="/crm" className={linkCls("/crm")} data-testid="nav-crm-link">
                <KanbanSquare className="w-4 h-4" />Leads
              </Link>
            )}
            <Link to="/clients" className={linkCls("/clients")} data-testid="nav-clients-link">
              <Briefcase className="w-4 h-4" />Clients
            </Link>
            <Link to="/follow-ups" className={linkCls("/follow-ups")} data-testid="nav-followups-link">
              <ListChecks className="w-4 h-4" />Follow-ups
            </Link>
            {(isAdmin || isConsultant) && (
              <Link to="/new" className={linkCls("/new")} data-testid="nav-new-assessment-link">
                <FilePlus2 className="w-4 h-4" />New assessment
              </Link>
            )}
            {isAdmin && (
              <Link to="/templates" className={linkCls("/templates")} data-testid="nav-templates-link">
                <Files className="w-4 h-4" />Templates
              </Link>
            )}
            {isAdmin && (
              <Link to="/users" className={linkCls("/users")} data-testid="nav-users-link">
                <UsersRound className="w-4 h-4" />Users
              </Link>
            )}
            {isAdmin && (
              <Link to="/settings" className={linkCls("/settings")} data-testid="nav-settings-link">
                <Settings className="w-4 h-4" />Settings
              </Link>
            )}
          </nav>

          <div className="flex items-center gap-3">
            <NotificationBell />
            <div className="hidden sm:flex flex-col items-end leading-tight">
              <span className="text-xs text-slate-500" data-testid="current-user-email">{user?.email}</span>
              <span
                className="text-[10px] uppercase font-semibold tracking-wider text-[#1A7F6E]"
                data-testid="current-user-role"
              >
                {isAdmin ? "Super admin" : isClientManager ? "Client manager" : "Consultant"}
              </span>
            </div>
            <button
              type="button"
              onClick={async () => { await logout(); navigate("/login"); }}
              className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#0D1B2A] px-2 py-1 rounded"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />Logout
            </button>
          </div>
        </div>
      </header>
      <main className="flex-1 print-container">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {children}
        </div>
      </main>
      <footer className="no-print border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 text-xs text-slate-500 flex justify-between">
          <span>{brandName} — Internal consulting tool</span>
          <span>nextprovider.com.au</span>
        </div>
      </footer>
    </div>
  );
}
